import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Fragment, useEffect } from "react";
import { toast } from 'react-toastify';

import Tab, { TabProps } from "../../../molecules/Tab";

import PageForm from "../../../templates/PageForm";
import { computed, useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import useFetch from "../../../../hooks/useFetch";
import { BsBank, BsBuilding } from "react-icons/bs";
import { LuBadgeDollarSign } from "react-icons/lu";
import { TbLetterA, TbLetterV } from "react-icons/tb";

import { removeErrorField } from "../../../../utils/formErrorUtils";
import LegalPersonFormTab1 from "./LegalPersonFormTab1";
import BankAccountFormTab from "./BankAccountFormTab";
import useCities from "../../../../hooks/useCities";
import useProvinces from "../../../../hooks/useProvinces";
import GuarantorRetailFormTab from "./GuarantorRetailFormTab";
import MiniLoading from "../../../ions/MiniLoading";
import ErrorMessage from "../../../atoms/ErrorMessage";

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/legal-persons`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const PROFILE_TO_TAB_MAPPING: Record<string, TabProps> = {
    'RECIPIENT': {
        id: 'tab-5',
        title: 'Contas bancárias',
        index: 3,
        icon: LuBadgeDollarSign,
    },
    'ISSUER': {
        id: 'tab-4',
        title: 'Emitente PJ',
        index: 4,
        icon: BsBuilding,
    },
    'GUARANTOR-WHOLESALE': {
        id: 'tab-3',
        title: 'Aval. atacado',
        index: 2,
        icon: TbLetterA,
    },
    'GUARANTOR-RETAIL': {
        id: 'tab-2',
        title: 'Aval. varejo',
        index: 1,
        icon: TbLetterV,
    },
};

type Form = {
    id: string;
    personId: string;
    govTaxId: string;
    fantasyName: string;
    legalName: string;
    zipcode: string;
    street: string;
    streetType: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    province: string;
    bankAccounts: any[];
    logo?: File | null;
    alias?: string;
    score?: number;
    limit?: number;
    guaranteePercentage?: number;
}

const LegalPersonPageEdit = () => {

    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();
    const isErrorOnLoadingGuarantorRetail = useSignal<boolean>(false);
    const isLoadingGuarantorRetail = useSignal<boolean>(false);

    const { personId = "" } = useParams();

    const navigate = useNavigate();
    const isSendingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const isLoadingForm = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const banks = useSignal<any[]>([]);

    const form = useSignal<Form>({
        id: '',
        personId: '',
        govTaxId: '',
        fantasyName: '',
        legalName: '',
        zipcode: '',
        street: '',
        streetType: '',
        number: '',
        complement: '',
        district: '',
        cityId: '',
        province: '',
        bankAccounts: [],
        guaranteePercentage: 100,
    });

    const tabItems = useSignal<TabProps[]>([
        { id: "tab-1", title: 'Dados cadastrais', index: 0, canEdit: true },
    ]);

    const [searchParams] = useSearchParams();
    const tabIndex: number = parseInt(searchParams.get('tab') || "0");
    const profiles = searchParams.get('profiles') || '';

    useEffect(() => {
        loadProvinces();

        const profilesArray = profiles.split(',');
        profilesArray.forEach(profile => {
            const tabItem = PROFILE_TO_TAB_MAPPING[profile];
            if (tabItem && !tabItems.value.find(item => item.id === tabItem.id)) {
                tabItems.value.push(tabItem);
            }
        });

    }, []);

    const loadProvinces = async () => {
        isLoadingForm.value = true;
        try {
            provinces.value = await fetchProvinces();
        } catch (error) {
            toast.error("Erro ao carregar os estados.");
        }
        isLoadingForm.value = false;
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const getCity = async (cityId: number) => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${process.env.REACT_APP_GEOLOCATION_API_URL}/cities/${cityId}?fields=id,name,province.initials`, {
                headers: {
                    "x-api-key": API_KEY,
                },
            });
            isLoadingForm.value = false;
            return await response.json();
        } catch (error) {
            toast.error("Erro ao carregar a cidade. Tente novamente!");
        }
        isLoadingForm.value = false;
    };

    const getPartnerGuarantorRetail = async (partnerId: string) => {
        isLoadingGuarantorRetail.value = true;
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKOFFICE_URL}/services/partner-guarantor-retail/${partnerId}`, {});
            if (response.ok) {
                return await response.json();
            }
            // 404 é normal para novos cadastros - não mostrar erro
            if (response.status === 404) {
                return null;
            }
        } catch (error) {
            // Apenas mostrar erro para problemas reais de rede/servidor
            console.warn("Erro ao carregar dados do avalista varejo:", error);
        } finally {
            isLoadingGuarantorRetail.value = false;
        }
    };

    const cancelForm = () => {
        navigate("/person/partners/0/100/id/ASC/");
    };

    const submitForm = async () => {
        // isSendingForm.value = true;

        // try {
        //     const response = await fetch(`${BASE_URL}`, {
        //         method: 'PUT',
        //         headers: {
        //             'x-api-key': API_KEY || '',
        //             'Content-Type': 'application/json',
        //         },
        //         body: JSON.stringify(form.value),
        //     });

        //     if (response.ok) {
        //         toast.success('Parceira cadastrada com sucesso!');
        //         navigate(-1);
        //     } else {
        //         const { errors, message } = await response.json();
        //         toast.error(message);
        //         errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
        //     }
        // } catch (error) {
        //     toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        // }
        // isSendingForm.value = false;
        window.alert('Ainda não implementado Sr. Machtura!');
    };

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${personId}/page-form`, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            form.value = {
                ...form.value,
                id: data.id,
                personId: data.personId,
                govTaxId: data.govTaxId,
                fantasyName: data.fantasyName,
                legalName: data.legalName,
                zipcode: data.zipcode,
                street: data.street,
                streetType: data.streetType,
                number: data.number,
                complement: data.complement,
                district: data.district,
                cityId: data.cityId,
                bankAccounts: data.bankAccounts,
            };
            banks.value = data.banks;

            getCity(data.cityId).then(city => {
                if (city) {
                    form.value = {
                        ...form.value,
                        province: city["province.initials"],
                    };

                    fetchCitiesWrapper(city["province.initials"]);
                }
            });

            getPartnerGuarantorRetail(data.personId).then((partner) => {
                // Não tratar dados vazios como erro - é normal para novos cadastros
                isErrorOnLoadingGuarantorRetail.value = false;
                if (partner) {
                    form.value = {
                        ...form.value,

                        logo: partner.logo,
                        alias: partner.alias,
                        guaranteePercentage: partner.guaranteePercentage,
                        score: partner.score,
                        limit: partner.limit,
                    };
                }
            });

            if (data.profiles) {
                data.profiles.forEach((profile: string) => {
                    const tabItem = PROFILE_TO_TAB_MAPPING[profile];
                    if (tabItem && !tabItems.value.find(item => item.id === tabItem.id)) {
                        tabItems.value.push({
                            ...tabItem,
                            canEdit: false,
                        });
                    }
                });
            }
        }
    }, [data]);

    const setTabIndex = (tabIndex: number) => {
        navigate(`?tab=${tabIndex}&profiles=${profiles}`);
    };

    const updateButtonDisable = computed(() => {
        return (isLoading || isSendingForm.value || form.value.fantasyName === '' ||
            form.value.legalName === '' || form.value.zipcode === '' || form.value.street === '' || form.value.streetType === '' ||
            form.value.number === '' || form.value.district === '' || form.value.cityId === '');
    });

    const changeForm = (field: string, value: string | File | null) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);
    };

    const newBankAccount = () => {
        // criar evento
        window.dispatchEvent(new CustomEvent("BankAccountFormTab:openModal", {}));
    };

    const submitGuarantorRetailForm = async () => {
        isSendingForm.value = true;

        try {
            const formData = new FormData();

            if (form.value.logo instanceof File) {
                formData.append('logo', form.value.logo);
            }

            formData.append('partnerId', form.value.personId || '');
            formData.append('govTaxId', form.value.govTaxId.replace(/\D/g, '') || '');
            formData.append('legalName', form.value.legalName || '');
            formData.append('fantasyName', form.value.fantasyName || '');
            formData.append('addressOne', form.value.street || '');
            formData.append('addressTwo', form.value.district || '');
            formData.append('addressNumber', form.value.number || '');
            formData.append('addressDetail', form.value.complement || '');
            formData.append('zipcode', form.value.zipcode.replace(/\D/g, '') || '');
            formData.append('cityId', form.value.cityId?.toString() || '');
            formData.append('phone', '00000000');
            formData.append('guaranteePercentage', form.value.guaranteePercentage?.toString() || '');
            formData.append('alias', form.value.alias || '');
            formData.append('loggedUser', 'jean.dobre'); // TODO obter  o usuario logado
            formData.append('score', form.value.score?.toString() || '');
            formData.append('limit', form.value.limit?.toString() || '');

            const response = await fetch(`${process.env.REACT_APP_BACKOFFICE_URL}/services/partner-guarantor-retail`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                toast.success('Avalista varejo atualizado com sucesso!');
                navigate("/person/partners/0/100/id/ASC/");
            } else {
                const { errors, message } = await response.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.fieldName, message: error.message })) : [];
            }
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        }
        isSendingForm.value = false;
    };

    return (
        <PageForm
            pageTitle={`#${form.value.id} - ${form.value.fantasyName}`}
            apiVersion={apiVersion.value}
            isLoading={isLoading || isSendingForm.value || isLoadingForm.value}
            breadcrumb={[
                { label: "Listagem de parceiras", url: "/person/partners/0/100/id/ASC/" },
                { label: `#${form.value.id} - ${form.value.fantasyName}`, url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">

                    <button
                        id="btn-cancel-edit"
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        title="Cancelar registro"
                        disabled={isLoading || isSendingForm.value}
                    >
                        Cancelar
                    </button>

                    {tabIndex == 0 &&
                        <button
                            id="btn-update-legal-person"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={submitForm}
                            title="Atualizar registro"
                            disabled={updateButtonDisable.value}

                        >
                            Atualizar
                        </button>
                    }

                    {tabIndex == 1 &&
                        <button
                            id="btn-save-guarantor"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={submitGuarantorRetailForm}
                            title="Salvar registro"
                            disabled={isLoading || isSendingForm.value}
                        >
                            Salvar
                        </button>
                    }

                    {tabIndex == 3 &&
                        <button
                            id="btn-add-bank-account"
                            className="flex items-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={newBankAccount}
                            title="Cadastrar nova conta bancária"
                        >
                            <BsBank className="mr-2" />
                            Nova conta
                        </button>
                    }
                </div>
            )}
        >
            <Fragment>
                <Tab tabItems={tabItems.value} tabIndex={tabIndex} setTabIndex={setTabIndex}></Tab>
                <div id="tab-content" className="overflow-y-auto">
                    {tabIndex == 0 &&
                        <LegalPersonFormTab1
                            key="tab1"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={true}
                        />
                    }

                    {tabIndex == 1 &&
                        <div className="p-4 m-4"
                            id="retail-guarantor">
                            {isLoadingGuarantorRetail.value ?
                                <p><MiniLoading /> Carregando dados do avalista varejo...</p>
                                : <>
                                    {
                                        isErrorOnLoadingGuarantorRetail.value ?
                                            <ErrorMessage message="Erro ao carregar os dados do avalista varejo. Volte para a listagem e tente novamente!" />
                                            :
                                            <GuarantorRetailFormTab
                                                form={form.value}
                                                errorFields={errorFields.value}
                                                changeForm={changeForm}
                                                editForm={tabItems.value.find(item => item.id === 'tab-2')?.canEdit || true}
                                            />
                                    }
                                </>
                            }
                        </div>}


                    {tabIndex == 2 &&
                        <div className="p-4 m-4"
                            id="wholesale-guarantor">
                            Avalista atacado

                        </div>}

                    {tabIndex == 3 &&
                        <BankAccountFormTab
                            key="tab3"
                            legalPersonId={personId}
                            provinces={provinces.value}
                            banks={banks.value}
                            bankAccounts={form.value.bankAccounts}
                        />}

                    {tabIndex == 4 &&
                        <div className="p-4 m-4"
                            id="issuer">
                            Emitente PJ
                        </div>}
                </div>
            </Fragment>

        </PageForm>
    );
};


export default LegalPersonPageEdit;